import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { MailIcon, AlertCircleIcon, CheckCircleIcon } from "lucide-react";

interface EmailVerificationProps {
  userId?: number;  // Optional userId in case we need to track which account is being verified
  onCancel: () => void;
}

export default function EmailVerification({ userId, onCancel }: EmailVerificationProps) {
  const [email, setEmail] = useState<string>("");
  const { resendVerificationMutation } = useAuth();
  const { toast } = useToast();
  
  const handleResendVerification = async () => {
    if (!email.trim()) {
      toast({
        title: "Email required",
        description: "Please enter your email address to receive a verification link.",
        variant: "destructive",
      });
      return;
    }
    
    resendVerificationMutation.mutate({ email });
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl text-center">Email Verification Required</CardTitle>
        <CardDescription className="text-center">
          Please verify your email address to continue
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <Alert className="mb-4">
          <AlertCircleIcon className="h-5 w-5" />
          <AlertTitle>You need to verify your email</AlertTitle>
          <AlertDescription>
            We've sent a verification link to your email address. Please check your inbox and click the link to verify your account.
          </AlertDescription>
        </Alert>
        
        <div className="space-y-4">
          <div className="text-center mb-4">
            <MailIcon className="mx-auto h-12 w-12 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              Didn't receive the email? Check your spam folder or request a new verification link.
            </p>
          </div>
          
          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium">
              Email Address
            </label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={resendVerificationMutation.isPending}
            />
          </div>
        </div>
      </CardContent>
      
      <CardFooter className="flex flex-col sm:flex-row gap-3">
        <Button
          variant="default"
          className="w-full"
          onClick={handleResendVerification}
          disabled={resendVerificationMutation.isPending}
        >
          {resendVerificationMutation.isPending ? "Sending..." : "Resend Verification Email"}
        </Button>
        <Button variant="outline" className="w-full" onClick={onCancel}>
          Back to Login
        </Button>
      </CardFooter>
    </Card>
  );
}